import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  Step,
  StepButton,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CheckIcon from "@mui/icons-material/Check";
import { DEFAULT_PASTEL_COLOR, PASTEL_COLORS } from "./pastelColors";

let nextTempId = -1;

export const newEmptyStep = () => ({
  id: nextTempId--,
  name: "",
  description: "",
  color: DEFAULT_PASTEL_COLOR,
});

function PastelColorSelect({ value, onChange }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const selected = value || DEFAULT_PASTEL_COLOR;
  const open = Boolean(anchorEl);

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Color
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="Seleccionar color"
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: selected,
            border: "2px solid",
            borderColor: "divider",
            cursor: "pointer",
            p: 0,
            display: "block",
          }}
        />
      </Stack>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 1,
            p: 1.5,
            width: 200,
          }}
        >
          {PASTEL_COLORS.map((hex) => {
            const isSelected = selected.toLowerCase() === hex.toLowerCase();
            return (
              <Box
                key={hex}
                component="button"
                type="button"
                onClick={() => {
                  onChange(hex);
                  setAnchorEl(null);
                }}
                aria-label={`Color ${hex}`}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: hex,
                  border: "1px solid",
                  borderColor: "rgba(0,0,0,0.12)",
                  cursor: "pointer",
                  p: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isSelected && (
                  <CheckIcon sx={{ fontSize: 16, color: "rgba(0,0,0,0.75)" }} />
                )}
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
}

/**
 * Shared vertical Stepper used both to author an OperationProcess's steps
 * (mode="edit") and to render/select a CPO's progress through one
 * (mode="select" allows clicking a step, mode="view" is read-only).
 */
export default function OperationProcessStepper({
  mode = "view",
  steps,
  onChange,
  activeStepId,
  onSelectStep,
}) {
  const handleFieldChange = (index, field, value) => {
    const next = steps.slice();
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  };

  const handleRemove = (index) => {
    onChange(steps.filter((_, i) => i !== index));
  };

  const handleMove = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= steps.length) return;
    const next = steps.slice();
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    onChange(next);
  };

  const handleAdd = () => {
    onChange([...steps, newEmptyStep()]);
  };

  if (mode === "edit") {
    return (
      <Box>
        <Stepper orientation="vertical" nonLinear>
          {steps.map((step, index) => (
            <Step key={step.id} active>
              <StepLabel>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      bgcolor: step.color || DEFAULT_PASTEL_COLOR,
                      border: "1px solid",
                      borderColor: "divider",
                      flexShrink: 0,
                    }}
                  />
                  <TextField
                    size="small"
                    label={`Paso ${index + 1}`}
                    value={step.name}
                    onChange={(e) => handleFieldChange(index, "name", e.target.value)}
                    fullWidth
                  />
                </Stack>
              </StepLabel>
              <StepContent TransitionProps={{ in: true }}>
                <Stack spacing={1}>
                  <TextField
                    size="small"
                    label="Descripción"
                    value={step.description}
                    onChange={(e) =>
                      handleFieldChange(index, "description", e.target.value)
                    }
                    multiline
                    minRows={2}
                    fullWidth
                  />
                  <PastelColorSelect
                    value={step.color}
                    onChange={(color) => handleFieldChange(index, "color", color)}
                  />
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      size="small"
                      disabled={index === 0}
                      onClick={() => handleMove(index, -1)}
                      aria-label="Mover arriba"
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={index === steps.length - 1}
                      onClick={() => handleMove(index, 1)}
                      aria-label="Mover abajo"
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemove(index)}
                      aria-label="Eliminar paso"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </StepContent>
            </Step>
          ))}
        </Stepper>
        <Button startIcon={<AddIcon />} onClick={handleAdd} sx={{ mt: 1 }}>
          Agregar paso
        </Button>
      </Box>
    );
  }

  const activeIndex = steps.findIndex((s) => s.id === activeStepId);

  return (
    <Stepper orientation="vertical" activeStep={activeIndex} nonLinear={mode === "select"}>
      {steps.map((step, index) => (
        <Step key={step.id} completed={activeIndex >= 0 && index < activeIndex}>
          {mode === "select" ? (
            <StepButton onClick={() => onSelectStep?.(step.id)}>
              {step.name}
            </StepButton>
          ) : (
            <StepLabel>{step.name}</StepLabel>
          )}
          {step.description ? (
            <StepContent TransitionProps={{ in: true }}>
              <Typography variant="body2" color="text.secondary">
                {step.description}
              </Typography>
            </StepContent>
          ) : null}
        </Step>
      ))}
    </Stepper>
  );
}
